import { z } from 'zod';
import { createModel, PartialData, QueryMany, partySchema } from '../../common';
import { languageSchema } from '../../common/language.schema';
import { floatNumSchema, nameSchema, objectIdSchema, phoneSchema } from '../../validation';
import { driverTypesSchema } from './driver.type.model';
import { driverLegalSchema } from './driver.legal.model';
import { driverVehicleSchema } from './driver.vehicle';
import { driverStatusSchema } from './driver.status.model';
import { UTagSharedSchema } from '../utag';
import { driverMetaSchema } from './driver.meta.model';

/** `driver db model` */
export const DriverSchema = createModel({
  status: driverStatusSchema,
  /** driver name */
  name: nameSchema,
  /**
   * Other names this driver is known by — a trade name typed in chat, the
   * name printed on a card. Learned only when the user confirms it or
   * Bayan accepts a number under it; a match here skips the name judge.
   */
  aliases: z.array(nameSchema).optional(),
  /** driver phone number */
  phone: phoneSchema,
  /**
   * Language the driver prefers to communicate in (`ar` / `en` / `ur`).
   * Drives the WhatsApp agent's outbound template + free-form language
   * for drivers without an app account (see `docs/ai/whatsapp-agent.md`).
   * Set by the carrier when adding the driver, or learned by the agent
   * from inbound message detection. Absent → per-message detection.
   */
  preferredLanguage: languageSchema.optional(),
  /** the rating of the driver */
  rating: floatNumSchema,
  /** driver type */
  type: driverTypesSchema,
  legal: driverLegalSchema.optional(),
  /**
   * the organization that a driver belongs to.
   *
   * if exists, then it means the driver belongs to an organization... most likely a `carrier_company`
   *
   * if null, then it means the driver is freelancer
   */
  org: partySchema.optional(),

  /**
   * The CRM client this driver belongs to, when he is NOT the Org's own
   * employee.
   *
   * A `target: 'client'` party snapshot; its `roles[]` are the client's
   * network roles — `carrier_company` (a subcontractor's driver a broker
   * dispatches), `broker`, `business_customer` (a customer's own truck
   * the Org manages), `individual_customer` (a one-man freelancer the
   * Org books). The relationship is "this driver belongs to that
   * client"; how each role is used is the Org's business, not the
   * schema's. Today the one consumer is the government waybill: at
   * assignment `OrderActionService.assignCarrier` copies this party
   * onto `Order.carrier`, so the وثيقة declares the client (its
   * `Client.legal.registrationId` as Bayan `carrier.moi`), never the
   * managing Org.
   *
   * `undefined` → own employee (or a freelancer with no `org`). `org`
   * keeps meaning "who manages this row" — listing, editing, dispatch —
   * and stays set for client drivers too, so one fleet list shows own
   * and clients' drivers side by side with this field as the badge.
   *
   * See `docs/adr/007-third-party-drivers.md`.
   */
  client: partySchema.optional(),

  /**
   * this identifies the driver from user collection.
   * if null, it means that the driver has not yet signed up
   */
  userId: objectIdSchema.optional(),
  /** the current vehicle that the driver has ...
   *
   * If empty, then the driver has not been assigned a vehicle yet
   */
  vehicle: driverVehicleSchema.optional(),
  UTags: z.array(UTagSharedSchema),

  /**
   * Agent-facing signal layer — see
   * [`docs/ai/user-signals.md`](../../../../../docs/ai/user-signals.md).
   * Mirrors `User.meta.location.accurate` for fleet-view fast path
   * (a Driver's last GPS point without a User join).
   */
  meta: driverMetaSchema.optional(),
});

/** `driver db model` */
export type Driver = z.infer<typeof DriverSchema.model>;
export type DriverData = z.infer<typeof DriverSchema.data>;
export type DriverPartial = z.infer<typeof DriverSchema.partial>;
export type DriverQueryMany = QueryMany<Driver>;
export type DriverUpdateOne = PartialData<Driver>;
